import * as React from "react";
import { useQuery } from "react-query";
import { api } from "utils/api";
import { RaffleItem, RaffleResp, SortedRaffleItems } from "../types";
import { raffleSort } from "../utils/raffleSort";

const getRaffleItems = (): Promise<RaffleItem[]> =>
  api("/rewards/raffles").then((res: RaffleResp[]) =>
    res.map(
      ({
        startDate,
        endDate,
        image,
        image300,
        image600,
        image1200,
        ...rest
      }) => ({
        ...rest,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        image: {
          url: image,
          url300: image300,
          url600: image600,
          url1200: image1200,
          contentType: `image/${image.split(".").reverse()[0]}`,
          importDate: "",
        },
      })
    )
  );

const useRaffleItems = () =>
  useQuery(["raffleItems"], getRaffleItems, {
    select: raffleSort,
  });

const useAllRaffleItems = () => useQuery(["raffleItems"], getRaffleItems);

const useRaffleItemsResort = (original: SortedRaffleItems) => {
  const [sorted, setSorted] = React.useState(original);

  // Store current time and update every second
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  // Sort the raffles based on current time and re-sort as needed
  React.useEffect(() => {
    // Two possibilities: 1) there is an active raffle, so see if it's over, or 2) there is not an
    // active raffle but there will be, so see if it's started
    if (
      (sorted.current && sorted.current.endDate < now) ||
      (sorted.future[0] && sorted.future[0].startDate <= now)
    ) {
      setSorted((old) => {
        const all = [...old.past, old.current, ...old.future].filter(
          (r): r is RaffleItem => r !== null
        );
        return raffleSort(all);
      });
    }
  }, [now, sorted]);

  return { now, ...sorted };
};

export { useAllRaffleItems, useRaffleItems, useRaffleItemsResort };
